import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { confirm } from '@/lib/confirm';
import { DashboardContent } from '@/layouts/dashboard';
import { FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import usePermission from '@/hooks/usePermission';
import { EssayRow } from './EssayRow';
import { REDACAO } from '@/pages/dashboard/home-teacher/teacher-character';

export default function EssayPlayfulTeacher({ uri }) {
  const client = useClient();

  const [courses, setCourses] = useState([]);
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);

  const canCreate = usePermission('Criar redação', 'Redação');

  const filters = useFormik({
    initialValues: {
      course: 'blank'
    }
  });

  const welcomeMessage = `Aqui estão as redações! Acompanhe as entregas, faça envios e realize as correções por aqui. ✍️`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesResponse, essaysResponse] = await Promise.all([client.fetch('courses/simple'), client.fetch('essays')]);

        if (coursesResponse.error) {
          toast.error('Erro ao carregar cursos');
        } else {
          setCourses(coursesResponse.data || []);
        }

        if (essaysResponse.error) {
          toast.error('Erro ao carregar redações');
        } else {
          setEssays(essaysResponse.data || []);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredEssays = essays.filter(essay => filters.values.course === 'blank' || essay?.['courses-ids']?.includes(Number(filters.values.course)));

  const deleteEssay = async id => {
    const { error } = await client.delete(['essays', id]);
    if (error) {
      toast.error('Erro ao deletar redação');
    } else {
      toast.success('Redação excluída!');
      setEssays(prevState => prevState.filter(item => item.id !== id));
    }
  };

  const handleDeleteEssay = async id => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta redação?',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      deleteEssay(id);
    }
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Redação',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-16"
        image={REDACAO}
        imageAlt="Personagem do professor"
        imageHeight={190}
        imageOffset={240}
        bubbleCorner="tl"
        bubbleMaxWidth={420}
        text={welcomeMessage}
      />

      <section
        className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: 'trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
        />

        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <FilterSelectNative
            label="Selecione um curso"
            value={filters.values.course}
            onChange={e => filters.setFieldValue('course', e.target.value)}
            blankLabel="Todos os cursos"
            options={courses.map(o => ({ id: o.id, name: o.title }))}
          />

          {canCreate && (
            <Link
              to="/redacao/criar/curso"
              className="block shrink-0 rounded-full px-6 py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
              style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
            >
              Nova redação
            </Link>
          )}
        </div>

        <hr className="relative my-6 border-t border-neutral-100" />

        {loading && <Loader />}

        {!loading && filteredEssays.length === 0 && (
          <EmptyState
            type="content"
            variant="plain"
            className="pt-16"
          />
        )}

        {!loading && filteredEssays.length > 0 && (
          <div className="relative flex flex-col gap-3">
            {filteredEssays.map((essay, index) => (
              <EssayRow
                key={essay.id}
                essay={essay}
                index={index}
                deleteEssay={handleDeleteEssay}
              />
            ))}
          </div>
        )}
      </section>
    </DashboardContent>
  );
}
